import { Component } from "react";
import { connect } from "react-redux";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { find } from "lodash-es";

import { amount } from "common/formats";
import { references } from "common/helpers/references";

import { ErrorView } from "common/components/error";
import { Placeholder } from "common/components/placeholder";
import { InfoMessage } from "common/components/message";

import {
    FETCH_BATCH_SUMMARY,
    RESET_RECON_STORE
} from "common/actions/reconciliation";

import { PazienError } from "common/helpers/errors";
import { Tabs } from "./tabs";
import { DrilldownTable } from "./drilldownTable";
import { BatchSummaryBar } from "./summaryBar";
import { Breadcrumbs, BatchDetails } from "./breadcrumbs";

const mapStateToProps = (state) => ({
    date_range: state.common.date_range,
    section: state.reconciliation.section,
    pivots: state.reconciliation.pivots,
    view: state.reconciliation.view,
    filters: state.reconciliation.filters,
    transferStatus: state.reconciliation.transferStatus,
    has_oct: state.common.merchant_capabilities.has_oct
});

const mapDispatchToProps = {
    RESET_RECON_STORE,
    FETCH_BATCH_SUMMARY
};
export const BatchDrilldown = connect(mapStateToProps, mapDispatchToProps)(class extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryData: void 0,
            status: "idle"
        };
        this.sections = [
            {
                name: "Net cash flow", alias: "all", key: "net_amount", disabled: true
            },
            {
                name: this.props.has_oct ? "Sales, refunds and payouts" : "Sales and refunds",
                alias: "settlements",
                key: "net_sale_refund_and_payout_amount",
                alt_name: "All sale and refund transactions"
            },
            {
                name: "Chargebacks", alias: "chargebacks", key: "net_chargeback_amount", alt_name: "All chargebacks"
            },
            {
                name: "Fees and taxes", alias: "fees", key: "net_fee_and_tax_amount", alt_name: "All fees and taxes"
            },
            {
                name: "Misc", alias: "misc", key: "net_adjustment_amount", alt_name: "All misc"
            }
        ];
        this.promises = [];
    }

    componentDidMount() {
        if (this.props.filters === "") {
            this.setState({
                status: "error",
                error: new PazienError({
                    header: "Navigation error",
                    message: "No filters specified in the URL. Check URL you are trying to navigate.",
                    custom: true,
                    customActions: [
                        {
                            label: "Reset",
                            onClick: (e) => {
                                e.preventDefault();
                                this.props.navigate({ display: { view: "flat" }, filters: "" });
                            }
                        }
                    ]
                })
            });

            return null;
        }
        this.setState({ status: "loading" }, this.fetchSummaryData);
        return null;
    }

    componentDidCatch(error) {
        this.promises.forEach((promise) => promise.cancel());
        this.setState({
            status: "error",
            error: new PazienError({ header: "Unable to load page", message: "Child component caught an error", originalError: error })
        });
    }

    componentWillUnmount() {
        this.promises.forEach((promise) => promise.cancel());
        this.promises = null;
    }

    fetchSummaryData = () => {
        // cancel previous pending requests
        this.promises.forEach((p) => p.cancel());
        const fetch_promise = FETCH_BATCH_SUMMARY({
            section: "all",
            paginate: false,
            pivots: ["acquirer", "funds_transfer_groupings", this.props.dateType, "stl_currency", "acquiring_entity"],
            q: this.props.filters
        }).then(
            (res) => {
                if (!res.results[0]) {
                    throw new PazienError({ header: "Bad request", message: "Summary not found. Check URL." });
                }
                return this.setState({ status: "idle", summaryData: res.results[0] });
            }
        ).catch(
            (error) => this.setState({
                status: "error",
                error: new PazienError({
                    originalError: error,
                    header: "Error",
                    message: "There was an internal error when rendering this page.",
                    custom: true,
                    customActions: [
                        {
                            label: "Reset",
                            onClick: (e) => { e.preventDefault(); this.props.navigate({ display: { view: "flat" }, filters: "" }); }
                        }
                    ]
                })
            })
        );
        this.promises.push(fetch_promise);
    };

    getMismatchMesage = (section) => {
        const { name } = find(this.sections, { alias: section });
        return `The sum of ${name.toLowerCase()} transactions ${section === "misc" ? "" : "(totaled in gray box at right) "}differs from the reported net settlement. Contact your Worldpay representative with a link to this page if you cannot reconcile your transactions.`;
    };

    render() {
        const { summaryData } = this.state; // use loader
        
        // sections to use field.js, getters
        const sections = this.sections.map((item) => ({ ...item, details: summaryData ? amount(summaryData?.[item.key] || 0, { currency: summaryData?.stl_currency || "" }) : <Placeholder noMargin size="medium"/> }));

        if (this.state.status === "error") {
            return (
                <ErrorView
                    error={this.state.error}
                    hideDetails
                    captureInSentry
                    custom
                />
            );
        }

        return (
            <div className="batch-drilldown-table">
                <Breadcrumbs
                    section={sections.find((s) => s.alias === this.props.section)}
                    transferStatus={this.props.transferStatus}
                    navigate={this.props.navigate}
                    dateType={this.props.dateType}
                    buildHref={this.props.buildHref}
                />
                <Tabs className="batch-drilldown" sections={sections} active={this.props.section} selectTab={this.props.navigate} customHeader={<BatchDetails batchData={summaryData} dateType={this.props.dateType} />} >
                    <BatchSummaryBar summaryData={summaryData} section={this.props.section} loading={this.state.status === "loading"} />
                    {summaryData?.mismatched_sections?.includes(this.props.section) && summaryData?.acquirer === "nap" && (
                    <InfoMessage icon={{ icon: faQuestionCircle }} >
                        {this.getMismatchMesage(this.props.section)}
                    </InfoMessage>
                    )}
                    <DrilldownTable
                        className="transactions"
                        noClickFiltering
                        apiSubresource={this.props.section}
                        queryParams={this.props.queryParams}
                        suggestedNamePrefix={`reconciliation_${summaryData && references.toAlias(summaryData.acquirer, "5db9d5a782d84c6c839df36f")}`.replace(/\W+/gi, "-")}
                        paginatorTop
                    />
                </Tabs>
            </div>
        );
    }
});
